import React from "react";
import { Document, Link, Page, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import {
  commonStyles,
  formatDate,
  formatPhoneNumber,
  isValidSummary,
  parseHtmlToText,
  renderBulletPoints,
} from "../Services/ReactPDFService.jsx";

// Simple Blush Template Styles - Clean design with blush pink accents
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    backgroundColor: "#ffffff",
    paddingTop: 30,
    paddingBottom: 35,
    paddingHorizontal: 0,
  },
  // Header with white background
  headerContainer: {
    backgroundColor: "#ffffff",
    paddingHorizontal: 40,
    paddingVertical: 24,
    marginTop: -30,
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#e8b4b8",
  },
  name: {
    fontSize: 28,
    fontWeight: 700,
    color: "#2d3748",
    marginBottom: 2,
    letterSpacing: 0.5,
    lineHeight: 1.2,
  },
  title: {
    fontSize: 40,
    color: "#718096",
    fontWeight: 300,
    marginBottom: 5,
    marginTop: 2,
  },
  contactRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    marginTop: 2,
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  contactIcon: {
    width: 9,
    height: 9,
    marginRight: 4,
  },
  contactText: {
    fontSize: 9,
    color: "#718096",
    lineHeight: 1,
  },
  // Main content area
  mainContent: {
    paddingHorizontal: 40,
  },
  // Section styling
  sectionContainer: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#2d3748",
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
    paddingBottom: 4,
  },
  // Experience items
  experienceItem: {
    marginBottom: 12,
  },
  experienceHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 3,
  },
  experienceLeft: {
    flex: 1,
  },
  experienceRight: {
    alignItems: "flex-end",
    minWidth: 100,
  },
  jobTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#2d3748",
  },
  companyLocation: {
    fontSize: 10,
    color: "#718096",
    fontStyle: "italic",
    marginTop: 1,
  },
  dateRange: {
    fontSize: 9,
    color: "#718096",
  },
  location: {
    fontSize: 9,
    color: "#a0aec0",
    marginTop: 1,
  },
  description: {
    fontSize: 10,
    lineHeight: 1.5,
    color: "#4a5568",
    marginTop: 4,
  },
  bulletPoint: {
    fontSize: 10,
    marginBottom: 3,
    paddingLeft: 12,
    lineHeight: 1.4,
    color: "#4a5568",
  },
  // Education items
  educationItem: {
    marginBottom: 10,
  },
  educationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 2,
  },
  degree: {
    fontSize: 11,
    fontWeight: 700,
    color: "#2d3748",
  },
  school: {
    fontSize: 10,
    color: "#718096",
    fontStyle: "italic",
  },
  gpa: {
    fontSize: 9,
    color: "#a0aec0",
    marginTop: 2,
  },
  // Two-column layout for Skills/Languages section
  twoColumnContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  columnLeft: {
    width: "48%",
  },
  columnRight: {
    width: "48%",
  },
  // Skills with progress bars (blush accent)
  skillItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
    gap: 8,
  },
  skillName: {
    fontSize: 9,
    color: "#2d3748",
    width: 70,
  },
  skillBarContainer: {
    flex: 1,
    height: 3,
    backgroundColor: "#e2e8f0",
    borderRadius: 2,
  },
  skillBar: {
    height: 3,
    backgroundColor: "#e8b4b8",
    borderRadius: 2,
  },
  // Languages section
  languageItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    gap: 10,
  },
  languageName: {
    fontSize: 10,
    color: "#2d3748",
    width: 60,
  },
  languageBarContainer: {
    flex: 1,
    height: 3,
    backgroundColor: "#e2e8f0",
    borderRadius: 2,
  },
  languageBar: {
    height: 3,
    backgroundColor: "#e8b4b8",
    borderRadius: 2,
  },
  // Awards section
  awardItem: {
    marginBottom: 8,
  },
  awardTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: "#2d3748",
  },
  awardDescription: {
    fontSize: 9,
    color: "#718096",
    fontStyle: "italic",
  },
  awardYear: {
    fontSize: 9,
    color: "#a0aec0",
  },
  // Tools section - 2 column grid
  toolsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 20,
  },
  toolItem: {
    width: "45%",
    marginBottom: 10,
  },
  toolName: {
    fontSize: 10,
    fontWeight: 700,
    color: "#2d3748",
    marginBottom: 2,
  },
  toolDescription: {
    fontSize: 9,
    color: "#718096",
  },
  // Projects section
  projectItem: {
    marginBottom: 10,
  },
  projectHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 3,
  },
  projectTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#2d3748",
  },
  projectTechContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginTop: 2,
  },
  projectTechLabel: {
    fontSize: 9,
    color: "#718096",
    fontStyle: "italic",
    marginRight: 4,
  },
  projectTechText: {
    fontSize: 9,
    color: "#718096",
    fontStyle: "italic",
    flex: 1,
    lineHeight: 1.4,
  },
  projectDescription: {
    fontSize: 10,
    lineHeight: 1.4,
    color: "#4a5568",
    marginTop: 3,
  },
  // Certifications
  certificationItem: {
    marginBottom: 8,
  },
  certificationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  certName: {
    fontSize: 10,
    fontWeight: 700,
    color: "#2d3748",
  },
  certAuthority: {
    fontSize: 9,
    color: "#718096",
  },
  // Most Proud Of / Achievements
  achievementItem: {
    marginBottom: 6,
  },
  achievementText: {
    fontSize: 10,
    lineHeight: 1.5,
    color: "#4a5568",
  },
  // Skills category display (fallback)
  skillCategory: {
    marginBottom: 4,
  },
  skillLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#2d3748",
  },
  skillCategoryText: {
    fontSize: 10,
    lineHeight: 1.4,
    color: "#4a5568",
  },
  // Summary with blush left border - no background
  summaryBox: {
    paddingLeft: 12,
    marginBottom: 16,
    borderLeftWidth: 3,
    borderLeftColor: "#e8b4b8",
    backgroundColor: "transparent",
  },
  summaryText: {
    fontSize: 10,
    lineHeight: 1.6,
    color: "#4a5568",
  },
});

const SimpleBlushTemplate = ({ resumeData, customStyles = {}, customSectionOrder = null }) => {
  const {
    personalInfo = {},
    workExperience = [],
    education = [],
    skills = {},
    projects = [],
    certifications = [],
    achievements = [],
    languages = [],
    awards = [],
    volunteering = [],
    interests = [],
    industryExpertise = [],
    publications = [],
    references = [],
    customSections = [],
    sectionTitles = {},
    tools = [],
    summary = "",
    professionalTitle = "",
  } = resumeData || {};

  // Create dynamic styles based on customizations
  const createDynamicStyles = (baseStyles, customizations) => {
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;

    if (!hasCustomizations) {
      return baseStyles;
    }

    const { fontSize, sectionSpacing, titleColor, subtitleColor, fontFamily } = customizations;

    const customStyleOverrides = {};

    if (fontFamily) {
      customStyleOverrides.page = { ...baseStyles.page, fontFamily };
    }

    if (fontSize) {
      customStyleOverrides.name = {
        ...baseStyles.name,
        fontSize: fontSize * 2,
      };
      customStyleOverrides.title = {
        ...baseStyles.title,
        fontSize: fontSize * 0.85,
      };
      customStyleOverrides.contactText = {
        ...baseStyles.contactText,
        fontSize: fontSize * 0.65,
      };
      customStyleOverrides.sectionTitle = {
        ...baseStyles.sectionTitle,
        fontSize: fontSize * 0.9,
      };
      customStyleOverrides.jobTitle = {
        ...baseStyles.jobTitle,
        fontSize: fontSize * 0.8,
      };
      customStyleOverrides.companyLocation = {
        ...baseStyles.companyLocation,
        fontSize: fontSize * 0.72,
      };
      customStyleOverrides.dateRange = {
        ...baseStyles.dateRange,
        fontSize: fontSize * 0.65,
      };
      customStyleOverrides.degree = {
        ...baseStyles.degree,
        fontSize: fontSize * 0.8,
      };
      customStyleOverrides.school = {
        ...baseStyles.school,
        fontSize: fontSize * 0.72,
      };
      customStyleOverrides.skillName = {
        ...baseStyles.skillName,
        fontSize: fontSize * 0.72,
      };
      customStyleOverrides.languageName = {
        ...baseStyles.languageName,
        fontSize: fontSize * 0.72,
      };
      customStyleOverrides.description = {
        ...baseStyles.description,
        fontSize: fontSize * 0.72,
      };
      customStyleOverrides.summaryText = {
        ...baseStyles.summaryText,
        fontSize: fontSize * 0.72,
      };
      customStyleOverrides.bulletPoint = {
        ...baseStyles.bulletPoint,
        fontSize: fontSize * 0.72,
      };
    }

    if (sectionSpacing) {
      customStyleOverrides.sectionContainer = {
        ...baseStyles.sectionContainer,
        marginBottom: sectionSpacing,
      };
      customStyleOverrides.experienceItem = {
        ...baseStyles.experienceItem,
        marginBottom: sectionSpacing * 0.75,
      };
    }

    if (titleColor) {
      customStyleOverrides.name = {
        ...baseStyles.name,
        color: titleColor,
      };
      customStyleOverrides.sectionTitle = {
        ...(customStyleOverrides.sectionTitle || baseStyles.sectionTitle),
        color: titleColor,
      };
      customStyleOverrides.jobTitle = {
        ...(customStyleOverrides.jobTitle || baseStyles.jobTitle),
        color: titleColor,
      };
      customStyleOverrides.degree = {
        ...(customStyleOverrides.degree || baseStyles.degree),
        color: titleColor,
      };
    }

    if (subtitleColor) {
      customStyleOverrides.skillBar = {
        ...baseStyles.skillBar,
        backgroundColor: subtitleColor,
      };
      customStyleOverrides.languageBar = {
        ...baseStyles.languageBar,
        backgroundColor: subtitleColor,
      };
      customStyleOverrides.summaryBox = {
        ...baseStyles.summaryBox,
        borderLeftColor: subtitleColor,
      };
    }

    return StyleSheet.create({
      ...baseStyles,
      ...customStyleOverrides,
    });
  };

  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Section titles with custom title support
  const defaultTitles = {
    summary: 'Profile',
    workExperience: 'Professional Experience',
    education: 'Education',
    skills: 'Skills',
    skillsLanguages: 'Skills & Languages',
    projects: 'Projects',
    certifications: 'Certifications',
    achievements: 'Achievements',
    languages: 'Languages',
    awards: 'Awards',
    volunteering: 'Volunteering',
    interests: 'Interests',
    industryExpertise: 'Industry Expertise',
    publications: 'Publications',
    references: 'References',
  };
  const getSectionTitle = (key) => sectionTitles[key] || defaultTitles[key] || key;

  // Default section order - includes all possible sections
  const defaultSectionOrder = [
    "summary",
    "workExperience",
    "education",
    "skillsLanguages",
    "skills",
    "projects",
    "certifications",
    "languages",
    "achievements",
    "awards",
    "volunteering",
    "interests",
    "industryExpertise",
    "publications",
    "references",
  ];

  // Include custom sections in the final order
  const customSectionIds = customSections
    .filter(cs => cs.id && cs.title && cs.items?.length > 0)
    .map(cs => cs.id);
  const baseSectionOrder = customSectionOrder || defaultSectionOrder;
  const finalSectionOrder = [
    ...baseSectionOrder,
    ...customSectionIds.filter(id => !baseSectionOrder.includes(id))
  ];

  // Combine all skills for display - handle all possible data formats
  const allSkills = (() => {
    // If skills is an array, use it directly
    if (Array.isArray(skills)) return skills;

    // If skills has a list property with items, use that
    if (skills.list && Array.isArray(skills.list) && skills.list.length > 0) {
      return skills.list;
    }

    // Combine from direct category properties (technical, soft, tools, other)
    const combinedSkills = [
      ...(skills.technical || []),
      ...(skills.soft || []),
      ...(skills.tools || []),
      ...(skills.other || []),
    ];

    // If we have combined skills, return them
    if (combinedSkills.length > 0) {
      return combinedSkills;
    }

    // If skills has categorized property (legacy format)
    if (skills.categorized) {
      return [
        ...(skills.categorized.technical || []),
        ...(skills.categorized.soft || []),
        ...(skills.categorized.tools || []),
        ...(skills.categorized.other || []),
      ];
    }

    // Return empty array if nothing found
    return [];
  })();

  // Helper to get skill name from string or object (component level)
  const getSkillName = (skill) => {
    if (typeof skill === "string") return skill;
    if (skill && typeof skill === "object") return skill.name || skill.skill || "";
    return "";
  };

  // Helper to get language name from string or object
  const getLanguageName = (lang) => {
    if (typeof lang === "string") return lang;
    if (lang && typeof lang === "object") return lang.name || lang.language || "";
    return "";
  };

  // Helper to get interest/expertise name from string or object
  const getItemName = (item) => {
    if (typeof item === "string") return item;
    if (item && typeof item === "object") return item.name || item.title || "";
    return "";
  };

  // Section components
  const sectionComponents = {
    summary: () =>
      summary &&
      isValidSummary(summary) && (
        <View style={mergedStyles.summaryBox}>
          <Text style={mergedStyles.summaryText}>{parseHtmlToText(summary)}</Text>
        </View>
      ),

    workExperience: () =>
      workExperience.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>Professional Experience</Text>
          {workExperience.map((job, index) => (
            <View key={index} style={mergedStyles.experienceItem}>
              <View style={mergedStyles.experienceHeader}>
                <View style={mergedStyles.experienceLeft}>
                  <Text style={mergedStyles.jobTitle}>{job.jobTitle}</Text>
                  <Text style={mergedStyles.companyLocation}>
                    {job.company}
                    {job.location ? ` | ${job.location}` : ""}
                  </Text>
                </View>
                <View style={mergedStyles.experienceRight}>
                  <Text style={mergedStyles.dateRange}>
                    {formatDate(job.startDate)} -{" "}
                    {job.isCurrentJob || job.endDate === "Present" || job.endDate === "Current" || !job.endDate
                      ? "present"
                      : formatDate(job.endDate)}
                  </Text>
                  {job.location && <Text style={mergedStyles.location}>{job.location}</Text>}
                </View>
              </View>
              {job.description && renderBulletPoints(parseHtmlToText(job.description), mergedStyles)}
            </View>
          ))}
        </View>
      ),

    education: () =>
      education.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>Education</Text>
          {education.map((edu, index) => (
            <View key={index} style={mergedStyles.educationItem}>
              <View style={mergedStyles.educationHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={mergedStyles.degree}>
                    {edu.degree}
                    {edu.fieldOfStudy ? `, ${edu.fieldOfStudy}` : ""}
                  </Text>
                  <Text style={mergedStyles.school}>{edu.school}</Text>
                </View>
                <View style={{ alignItems: "flex-end" }}>
                  {edu.location && <Text style={mergedStyles.location}>{edu.location}</Text>}
                </View>
              </View>
              {edu.gpa && <Text style={mergedStyles.gpa}>GPA: {edu.gpa}</Text>}
            </View>
          ))}
        </View>
      ),

    skillsLanguages: () => {
      const hasSkills = allSkills.length > 0;
      const hasLanguages = languages && languages.length > 0;

      if (!hasSkills && !hasLanguages) return null;

      // Limit to top 10 skills and split into left and right columns
      const topSkills = allSkills.slice(0, 10);
      const totalSkills = topSkills.length;
      const leftCount = Math.ceil(totalSkills / 2);
      const leftSkills = topSkills.slice(0, leftCount);
      const rightSkills = topSkills.slice(leftCount);

      return (
        <View style={mergedStyles.sectionContainer} wrap={false}>
          {/* Skills Section - Two Columns (Top 10 only) */}
          {hasSkills && (
            <>
              <Text style={mergedStyles.sectionTitle}>Skills</Text>
              <View style={mergedStyles.twoColumnContainer}>
                {/* Left Column */}
                <View style={mergedStyles.columnLeft}>
                  {leftSkills.map((skill, index) => (
                    <View key={`left-${index}`} style={mergedStyles.skillItem}>
                      <Text style={mergedStyles.skillName}>{getSkillName(skill)}</Text>
                      <View style={mergedStyles.skillBarContainer}>
                        <View style={[mergedStyles.skillBar, { width: "90%" }]} />
                      </View>
                    </View>
                  ))}
                </View>
                {/* Right Column */}
                <View style={mergedStyles.columnRight}>
                  {rightSkills.map((skill, index) => (
                    <View key={`right-${index}`} style={mergedStyles.skillItem}>
                      <Text style={mergedStyles.skillName}>{getSkillName(skill)}</Text>
                      <View style={mergedStyles.skillBarContainer}>
                        <View style={[mergedStyles.skillBar, { width: "90%" }]} />
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            </>
          )}

          {/* Languages Section */}
          {hasLanguages && (
            <View style={{ marginTop: hasSkills ? 16 : 0 }}>
              <Text style={mergedStyles.sectionTitle}>Languages</Text>
              <View style={mergedStyles.twoColumnContainer}>
                <View style={mergedStyles.columnLeft}>
                  {languages.slice(0, Math.ceil(languages.length / 2)).map((lang, index) => (
                    <View key={index} style={mergedStyles.languageItem}>
                      <Text style={mergedStyles.languageName}>{getLanguageName(lang)}</Text>
                      <View style={mergedStyles.languageBarContainer}>
                        <View
                          style={[
                            mergedStyles.languageBar,
                            {
                              width:
                                lang.proficiency === "Native" || lang.proficiency === "Fluent"
                                  ? "100%"
                                  : lang.proficiency === "Advanced"
                                  ? "85%"
                                  : lang.proficiency === "Intermediate"
                                  ? "65%"
                                  : "45%",
                            },
                          ]}
                        />
                      </View>
                    </View>
                  ))}
                </View>
                <View style={mergedStyles.columnRight}>
                  {languages.slice(Math.ceil(languages.length / 2)).map((lang, index) => (
                    <View key={index} style={mergedStyles.languageItem}>
                      <Text style={mergedStyles.languageName}>{getLanguageName(lang)}</Text>
                      <View style={mergedStyles.languageBarContainer}>
                        <View
                          style={[
                            mergedStyles.languageBar,
                            {
                              width:
                                lang.proficiency === "Native" || lang.proficiency === "Fluent"
                                  ? "100%"
                                  : lang.proficiency === "Advanced"
                                  ? "85%"
                                  : lang.proficiency === "Intermediate"
                                  ? "65%"
                                  : "45%",
                            },
                          ]}
                        />
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            </View>
          )}
        </View>
      );
    },

    skills: () => {
      const hasSkills = allSkills.length > 0;

      if (!hasSkills) return null;

      // Limit to top 10 skills and split into left and right columns
      const topSkills = allSkills.slice(0, 10);
      const totalSkills = topSkills.length;
      const leftCount = Math.ceil(totalSkills / 2);
      const leftSkills = topSkills.slice(0, leftCount);
      const rightSkills = topSkills.slice(leftCount);

      return (
        <View style={mergedStyles.sectionContainer} wrap={false}>
          <Text style={mergedStyles.sectionTitle}>Skills</Text>
          <View style={mergedStyles.twoColumnContainer}>
            {/* Left Column */}
            <View style={mergedStyles.columnLeft}>
              {leftSkills.map((skill, index) => (
                <View key={`left-${index}`} style={mergedStyles.skillItem}>
                  <Text style={mergedStyles.skillName}>{getSkillName(skill)}</Text>
                  <View style={mergedStyles.skillBarContainer}>
                    <View style={[mergedStyles.skillBar, { width: "90%" }]} />
                  </View>
                </View>
              ))}
            </View>
            {/* Right Column */}
            <View style={mergedStyles.columnRight}>
              {rightSkills.map((skill, index) => (
                <View key={`right-${index}`} style={mergedStyles.skillItem}>
                  <Text style={mergedStyles.skillName}>{getSkillName(skill)}</Text>
                  <View style={mergedStyles.skillBarContainer}>
                    <View style={[mergedStyles.skillBar, { width: "90%" }]} />
                  </View>
                </View>
              ))}
            </View>
          </View>
        </View>
      );
    },

    projects: () =>
      projects.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>Projects</Text>
          {projects.map((project, index) => (
            <View key={index} style={mergedStyles.projectItem}>
              <View style={mergedStyles.projectHeader}>
                <Text style={mergedStyles.projectTitle}>{project.name}</Text>
                {(project.url || project.link) && (
                  <Link
                    src={
                      (project.url || project.link).startsWith("http")
                        ? project.url || project.link
                        : `https://${project.url || project.link}`
                    }
                    style={{
                      fontSize: 9,
                      color: "#e8b4b8",
                      marginLeft: 4,
                      textDecoration: "underline",
                    }}
                  >
                    [Link]
                  </Link>
                )}
              </View>
              {project.technologies && (
                <View style={mergedStyles.projectTechContainer}>
                  <Text style={mergedStyles.projectTechLabel}>Technologies:</Text>
                  <Text style={mergedStyles.projectTechText}>{project.technologies}</Text>
                </View>
              )}
              {project.description && (
                <View style={{ marginTop: 4 }}>
                  {renderBulletPoints(parseHtmlToText(project.description), mergedStyles)}
                </View>
              )}
            </View>
          ))}
        </View>
      ),

    certifications: () =>
      certifications.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>Certifications</Text>
          {certifications.map((cert, index) => (
            <View key={index} style={mergedStyles.certificationItem}>
              <View style={mergedStyles.certificationHeader}>
                <Text style={mergedStyles.certName}>{cert.name}</Text>
                {cert.date && <Text style={mergedStyles.dateRange}>{formatDate(cert.date)}</Text>}
              </View>
              <Text style={mergedStyles.certAuthority}>{cert.authority || cert.issuer}</Text>
            </View>
          ))}
        </View>
      ),

    achievements: () =>
      achievements.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>Achievements</Text>
          {achievements.map((achievement, index) => (
            <View key={index} style={mergedStyles.achievementItem}>
              {achievement.description && (
                <Text style={mergedStyles.achievementText}>{parseHtmlToText(achievement.description)}</Text>
              )}
            </View>
          ))}
        </View>
      ),

    awards: () =>
      awards &&
      awards.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('awards')}</Text>
          {awards.map((award, index) => (
            <View key={index} style={mergedStyles.awardItem}>
              <Text style={mergedStyles.awardTitle}>{award.title || award.name}</Text>
              {award.description && <Text style={mergedStyles.awardDescription}>{award.description}</Text>}
              {award.year && <Text style={mergedStyles.awardYear}>{award.year}</Text>}
            </View>
          ))}
        </View>
      ),

    volunteering: () =>
      volunteering &&
      volunteering.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('volunteering')}</Text>
          {volunteering.map((vol, index) => (
            <View key={index} style={mergedStyles.experienceItem}>
              <View style={mergedStyles.experienceHeader}>
                <View style={mergedStyles.experienceLeft}>
                  <Text style={mergedStyles.jobTitle}>{vol.role || vol.position}</Text>
                  <Text style={mergedStyles.companyLocation}>{vol.organization}</Text>
                </View>
                <View style={mergedStyles.experienceRight}>
                  {(vol.startDate || vol.endDate) && (
                    <Text style={mergedStyles.dateRange}>
                      {formatDate(vol.startDate)} - {vol.endDate === "Present" ? "Present" : formatDate(vol.endDate)}
                    </Text>
                  )}
                </View>
              </View>
              {vol.description && renderBulletPoints(parseHtmlToText(vol.description), mergedStyles)}
            </View>
          ))}
        </View>
      ),

    interests: () =>
      interests &&
      interests.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('interests')}</Text>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            {interests.map((interest, index) => (
              <Text key={index} style={{ fontSize: 10, color: "#4a5568" }}>
                • {getItemName(interest)}
              </Text>
            ))}
          </View>
        </View>
      ),

    industryExpertise: () =>
      industryExpertise &&
      industryExpertise.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('industryExpertise')}</Text>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            {industryExpertise.map((expertise, index) => (
              <Text key={index} style={{ fontSize: 10, color: "#4a5568" }}>
                • {getItemName(expertise)}
              </Text>
            ))}
          </View>
        </View>
      ),

    publications: () =>
      publications &&
      publications.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('publications')}</Text>
          {publications.map((pub, index) => (
            <View key={index} style={mergedStyles.experienceItem}>
              <Text style={mergedStyles.jobTitle}>{pub.title}</Text>
              {pub.publisher && <Text style={mergedStyles.companyLocation}>{pub.publisher}</Text>}
              {pub.date && <Text style={mergedStyles.dateRange}>{formatDate(pub.date)}</Text>}
              {pub.description && <Text style={mergedStyles.description}>{parseHtmlToText(pub.description)}</Text>}
            </View>
          ))}
        </View>
      ),

    references: () =>
      references &&
      references.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('references')}</Text>
          {references.map((ref, index) => (
            <View key={index} style={mergedStyles.experienceItem}>
              <Text style={mergedStyles.jobTitle}>{ref.name}</Text>
              {ref.title && <Text style={mergedStyles.companyLocation}>{ref.title}</Text>}
              {ref.company && <Text style={mergedStyles.companyLocation}>{ref.company}</Text>}
              {ref.phone && <Text style={mergedStyles.dateRange}>{ref.phone}</Text>}
              {ref.email && <Text style={mergedStyles.dateRange}>{ref.email}</Text>}
            </View>
          ))}
        </View>
      ),

    // Dynamic custom sections
    ...customSections.reduce((acc, customSection) => {
      if (customSection.id && customSection.title && customSection.items?.length > 0) {
        acc[customSection.id] = () => (
          <View style={mergedStyles.sectionContainer}>
            <Text style={mergedStyles.sectionTitle}>{customSection.title}</Text>
            {customSection.items.map((item, itemIndex) => (
              <View key={itemIndex} style={{ marginBottom: 4 }}>
                {item.content && <Text style={mergedStyles.description}>{parseHtmlToText(item.content)}</Text>}
              </View>
            ))}
          </View>
        );
      }
      return acc;
    }, {}),
  };

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page} wrap={true}>
        {/* Header with dark navy banner - no photo */}
        <View style={mergedStyles.headerContainer}>
          <Text style={mergedStyles.name}>
            {personalInfo.firstName} {personalInfo.lastName}
          </Text>
          {(professionalTitle || (workExperience.length > 0 && workExperience[0].jobTitle)) && (
            <Text style={mergedStyles.title}>{professionalTitle || workExperience[0].jobTitle}</Text>
          )}
          <View style={mergedStyles.contactRow}>
            {personalInfo.location && (
              <View style={mergedStyles.contactItem}>
                <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
                  <Path
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                    fill="#e8b4b8"
                  />
                </Svg>
                <Text style={mergedStyles.contactText}>{personalInfo.location}</Text>
              </View>
            )}
            {personalInfo.email && (
              <View style={mergedStyles.contactItem}>
                <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
                  <Path
                    d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                    fill="#e8b4b8"
                  />
                </Svg>
                <Text style={mergedStyles.contactText}>{personalInfo.email}</Text>
              </View>
            )}
            {personalInfo.phone && (
              <View style={mergedStyles.contactItem}>
                <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
                  <Path
                    d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
                    fill="#e8b4b8"
                  />
                </Svg>
                <Text style={mergedStyles.contactText}>{formatPhoneNumber(personalInfo.phone)}</Text>
              </View>
            )}
            {personalInfo.linkedin && (
              <View style={mergedStyles.contactItem}>
                <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
                  <Path
                    d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"
                    fill="#e8b4b8"
                  />
                </Svg>
                <Text style={mergedStyles.contactText}>{personalInfo.linkedin}</Text>
              </View>
            )}
            {personalInfo.website && (
              <View style={mergedStyles.contactItem}>
                <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
                  <Path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"
                    fill="#e8b4b8"
                  />
                </Svg>
                <Text style={mergedStyles.contactText}>{personalInfo.website}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Main Content */}
        <View style={mergedStyles.mainContent}>
          {finalSectionOrder.map((sectionId, index) => {
            const SectionComponent = sectionComponents[sectionId];
            return SectionComponent ? <View key={`${sectionId}-${index}`}>{SectionComponent()}</View> : null;
          })}
        </View>
      </Page>
    </Document>
  );
};

export default SimpleBlushTemplate;
